import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import parse, { domToReact } from 'html-react-parser';
import parseDom from 'html-dom-parser';
import moment from 'moment';

import useAPI from '../../hooks/useAPI';
import useToast from '../../hooks/useToast';
import useUserContext from '../../hooks/useUserContext';

import BackBtn from '../../components/UI/BackBtn';
import NewsEditForm from '../../components/News/Edit/Form';
import NewsReadPost from '../../components/News/Read/Post';
import {NewsMode} from './modes/NewsMode';
import {MilestoneNewsMode} from './modes/MilestoneNewsMode';

import '../Page.less';
import LoadingGuard from '../../components/common/LoadingGuard';
import { isAdmin } from '../../services/rolesSvc';

export default function NewsEdit() {
  const { id } = useParams();
  const history = useHistory();
  const api = useAPI();
  const userCtx = useUserContext();
  const addToast = useToast();

  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const [mode, setMode] = useState(urlParams.get('milestone')
    ? new MilestoneNewsMode({ api, id: urlParams.get('milestone'), navCtx: { strategyId: urlParams.get('strategyId'), policyId: urlParams.get('policyId'), projId: urlParams.get('projId')}})
    : new NewsMode({ api, id }));

  const [error, setError] = useState(null);
  const [isShowPreview, setIsShowPreview] = useState(true);
  const [postHeader, setPostHeader] = useState('');
  const [postContent, setPostContent] = useState('');
  const [postUploadDate, setPostUploadDate] = useState(moment().format('YYYY-MM-DDTHH:mm:ss'));
  const [imageFileId, setImageFileId] = useState();
  const [postImage, setPostImage] = useState(null);
  const [markToDelete, setMarkToDelete] = useState(false);
  const [tags, setTags] = useState({});
  const [tagsAPI, setTagsAPI] = useState([]);

  const handleFileOpen = async (item) =>
    await api.download(`/minioFiles/download/${item.id}`, item.name);

  const changeTags = (tag) => {
    const newTags = { ...tags };
    newTags[tag] = !tags[tag];
    if (!newTags[tag]) delete newTags[tag];
    setTags(newTags);
  };

  useEffect(async () => {
    const newsResp = await api.get(`/news/tags${id === 'new' ? '' : '/' + id}`);
    setTagsAPI(newsResp);
  }, []);

  useEffect(async () => {
    if (!mode.canEdit(userCtx)) {
      setError('Недостаточно прав для просмотра страницы');
      return;
    }
    if (mode.id && mode.id !== 'new') {
      const fetchedPost = await mode.get(mode.id);//api.get(`/news/${id}`);
      if (fetchedPost && !fetchedPost.error) {
        setPostHeader(fetchedPost.header);
        setPostContent(fetchedPost.post);
        setPostUploadDate(moment(fetchedPost.uploadDate).format('YYYY-MM-DDTHH:mm:ss'));
        setImageFileId(fetchedPost.imageFileId);
        const tagsPost = {};
        fetchedPost.tags.forEach((tag) => {
          tagsPost[tag.id ?? tag] = true;
        });
        setTags(tagsPost);
      }
    }
  }, []);

  const previewBtnLabel = useMemo(() => {
    return isShowPreview ? 'Скрыть предпросмотр' : 'Показать предпросмотр'
  }, [isShowPreview]);

  const validateFields = () => {
    if (!postHeader) {
      addToast.error('Необходимо указать заголовок новости');
      return false;
    }

    if (postHeader.length > 500) {
      addToast.error('Длина заголовка не может превышать 500 символов');
      return false;
    }

    if (!postContent) {
      addToast.error('Необходимо заполнить содержание новости');
      return false;
    }

    if (!postUploadDate) {
      addToast.error('Необходимо указать дату публикации');
      return false;
    }

    return true;
  };

  const onSubmit = async () => {
    if (!validateFields()) return;

    const dto = {
      header: postHeader,
      post: postContent,
      uploadDate: postUploadDate,
      removeImage: markToDelete,
      tags: Array.from(Object.keys(tags)),
    };

    const result = await mode.settings.save.action(dto, postImage);//api.postFileFixed(url, dto, postImage);

    if (result && !result.error) {
      mode.settings.save.afterActionMessages.forEach(message => addToast.success(message));//'Новость успешно сохранена');
      history.push(mode.settings.callbackUrl);//'/news');
    } else {
      addToast.error('При сохранении новости произошла ошибка');
    }
  };

  const parseOptions = {
    replace: (domNode) => {
      const nodeData = parseDom(domNode?.data || '');
      const hasFileDownloadTag = nodeData.find((node) => node?.attribs?.filedownload);

      if (domNode?.attribs?.filedownload) {
        return (
          <a
            className="" // нужно добавить хоть какой-то класс, иначе стили сотрутся https://github.com/twbs/bootstrap/issues/19402
            style={{ cursor: 'pointer' }}
            onClick={() =>
              handleFileOpen({
                id: domNode.attribs.id,
                type: 'file',
                name: domNode.attribs.filename,
              })}
          >
            {domNode.children[0]?.data}
          </a>
        );
      }
      if (hasFileDownloadTag) {
        return <>{domToReact(nodeData, parseOptions)}</>;
      }
    },
  };

  return (
    <section className="page news-page">
      <div className="container">
        <BackBtn />
        <LoadingGuard error={error}>
          <h2>Редактирование новости</h2>
          <NewsEditForm
            postId={id}
            setPostHeader={setPostHeader}
            postHeader={postHeader}
            setPostContent={setPostContent}
            postContent={postContent}
            setPostUploadDate={setPostUploadDate}
            postUploadDate={postUploadDate}
            setPostImage={setPostImage}
            postImage={postImage}
            imageFileId={imageFileId}
            setMarkToDelete={setMarkToDelete}
            markToDelete={markToDelete}
          />
          <div>
            <div style={{ marginBottom: '0.5rem' }}>{isAdmin(userCtx) ? "Добавьте рубрики для этой новости" : "Рубрики для этой новости"}</div>
            <div className="news-tags-wrapper">
              {isAdmin(userCtx) 
                ? tagsAPI.map((e, i) => (
                  <button
                    key={i}
                    onClick={() => changeTags(e.id)}
                    className={
                      tags[e.id]
                        ? 'news-tags-button news-tags-button-active'
                        : 'news-tags-button'
                    }
                  >
                    {e.name}
                    {tags[e.id] && (
                    <svg
                      style={{ marginLeft: '9px' }}
                      width="9"
                      height="10"
                      viewBox="0 0 9 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M3.25588 4.45282L1.16526e-05 7.86671L1.30305 9.10943L4.5 5.75732L7.69695 9.10943L8.99999 7.86671L5.74412 4.45282L8.80566 1.24268L7.50263 -4.62827e-05L4.5 3.14831L1.49737 -4.62827e-05L0.194336 1.24268L3.25588 4.45282Z"
                        fill="#9CA6B0"
                      />
                    </svg>
                    )}
                  </button>
                ))
                : tagsAPI
                  .filter((e) => tags[e.id])
                  .map((e, i) => (
                    <button
                      key={i}
                      className='news-tags-button news-tags-button-active'
                    >
                      {e.name}
                      <svg
                        width="9"
                        height="10"
                        viewBox="0 0 9 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                      </svg>
                    </button>
                  ))
              }
            </div>
          </div>

          <div style={toolbarContainer}>
            <div style={toolbarItem}>
              <Button onClick={onSubmit}>
                {mode.settings.save.title}
              </Button>
            </div>
            <div style={toolbarItem}>
              <Button className="ml-2" variant="outline-secondary" onClick={() => setIsShowPreview((prev) => !prev)}>
                {previewBtnLabel}
              </Button>
            </div>
          </div>
          { isShowPreview && (
            <NewsReadPost
              header={postHeader || 'Заголовок новости'}
              content={parse(postContent || 'Содержание новости', parseOptions)}></NewsReadPost>
          )}
        </LoadingGuard>
      </div>
    </section>
  );
}

const toolbarContainer = { display: 'flex', marginTop: '24px', marginBottom: '24px' };
const toolbarItem = { marginRight: '16px' };
